/**
 * Raw Products Page
 *
 * Lists raw products (matières premières) with filters, pagination and drawer detail.
 * Supports deep linking via URL params and query strings.
 */

import { useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Plus, Upload } from 'lucide-react';
import { fetchAllForExport } from '@bill/ui';
import { PageHeader } from '@/components/ui';
import { Button } from '@/components/ui';
import { useDebounce, useUrlParams, useDrawerRoute, usePermission, useCanSeeFinancials } from '@/hooks';
import { RawProductsFilters } from '../components/RawProductsFilters';
import { RawProductsTable } from '../components/RawProductsTable';
import { RawProductDrawer } from '../components/RawProductDrawer';
import { CreateRawProductModal } from '../components/CreateRawProductModal';
import { useRawProducts } from '../hooks/useRawProducts';
import { rawProductsService } from '../services/raw-products.service';
import type { MarketType, QueryRawProductsDto } from '@bstock/shared';

type SortField = NonNullable<QueryRawProductsDto['sortBy']>;
type SortOrder = NonNullable<QueryRawProductsDto['sortOrder']>;

const DEFAULT_LIMIT = 20;

export default function RawProductsPage() {
  // Permissions
  const canCreate = usePermission('raw-products.create');
  const showFinancials = useCanSeeFinancials();

  // Navigation for deep linking
  const navigate = useNavigate();
  const location = useLocation();

  // Modal is open if URL ends with /create
  const isCreateModalOpen = location.pathname.endsWith('/create');

  // URL-synced filters using reusable hook
  const { params, setParam, setParams, resetParams } = useUrlParams({
    search: { default: '' },
    categoryId: { default: '' },
    marketTypes: { default: '' }, // comma-separated
    isActive: { default: '' }, // 'true' | 'false' | ''
    page: { default: 1, type: 'number' },
    limit: { default: DEFAULT_LIMIT, type: 'number' },
    sortBy: { default: 'name' as SortField },
    sortOrder: { default: 'asc' as SortOrder },
  });

  // URL-synced drawer state
  const drawer = useDrawerRoute('/products/raw', { paramName: 'productId' });

  // Debounce search
  const debouncedSearch = useDebounce(params.search, 300);

  // Parse marketTypes from comma-separated string
  const marketTypesArray: MarketType[] = params.marketTypes
    ? (params.marketTypes.split(',') as MarketType[])
    : [];

  // Parse isActive
  const isActiveValue = params.isActive === '' ? undefined : params.isActive === 'true';

  // Data fetching
  const { data, isLoading, error, isPlaceholderData } = useRawProducts({
    page: params.page,
    limit: params.limit,
    search: debouncedSearch || undefined,
    categoryId: params.categoryId || undefined,
    marketTypes: marketTypesArray.length > 0 ? marketTypesArray : undefined,
    isActive: isActiveValue,
    sortBy: params.sortBy as SortField,
    sortOrder: params.sortOrder as SortOrder,
  });

  // Check if filters are active
  const hasActiveFilters = Boolean(
    params.search ||
    params.categoryId ||
    params.marketTypes ||
    params.isActive
  );

  // Handlers
  const handleSearchChange = useCallback((value: string) => {
    setParams({ search: value, page: 1 });
  }, [setParams]);

  const handleCategoryChange = useCallback((categoryId: string | undefined) => {
    setParams({ categoryId: categoryId || '', page: 1 });
  }, [setParams]);

  const handleMarketTypesChange = useCallback((types: MarketType[]) => {
    setParams({ marketTypes: types.join(','), page: 1 });
  }, [setParams]);

  const handleIsActiveChange = useCallback((value: boolean | undefined) => {
    setParams({ isActive: value === undefined ? '' : value.toString(), page: 1 });
  }, [setParams]);

  const handlePageChange = useCallback((page: number) => {
    setParam('page', page);
  }, [setParam]);

  const handleLimitChange = useCallback((limit: number) => {
    setParams({ limit, page: 1 });
  }, [setParams]);

  const handleReset = useCallback(() => {
    resetParams();
  }, [resetParams]);

  const handleRowClick = useCallback((productId: string) => {
    drawer.open(productId);
  }, [drawer]);

  const handleDrawerClose = useCallback((open: boolean) => {
    if (!open) drawer.close();
  }, [drawer]);

  const handleCreateClick = useCallback(() => {
    navigate('/products/raw/create');
  }, [navigate]);

  const handleCreateModalClose = useCallback(() => {
    navigate('/products/raw');
  }, [navigate]);

  const handleCreateSuccess = useCallback((productId: string) => {
    navigate('/products/raw'); // Close the modal
    drawer.open(productId); // Open drawer with the new product
  }, [navigate, drawer]);

  const handleSort = useCallback((field: SortField, order: SortOrder) => {
    setParams({ sortBy: field, sortOrder: order, page: 1 });
  }, [setParams]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <PageHeader
          title="Produits bruts"
          subtitle="Gestion du catalogue de produits et matieres premieres"
        />
        <div className="flex items-center gap-3">
          {canCreate && (
            <>
              <Button variant="secondary" disabled>
                <Upload className="w-4 h-4" />
                Importer
              </Button>
              <Button variant="primary" onClick={handleCreateClick}>
                <Plus className="w-4 h-4" />
                Creer produit
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Filters */}
      <RawProductsFilters
        search={params.search}
        onSearchChange={handleSearchChange}
        categoryId={params.categoryId}
        onCategoryChange={handleCategoryChange}
        marketTypes={marketTypesArray}
        onMarketTypesChange={handleMarketTypesChange}
        isActive={isActiveValue}
        onIsActiveChange={handleIsActiveChange}
        onReset={handleReset}
        hasActiveFilters={hasActiveFilters}
      />

      {/* Table */}
      <RawProductsTable
        products={data?.data ?? []}
        pagination={data?.pagination ?? { page: 1, limit: params.limit, total: 0, totalPages: 0 }}
        isLoading={isLoading || isPlaceholderData}
        error={error}
        onRowClick={handleRowClick}
        onPageChange={handlePageChange}
        onLimitChange={handleLimitChange}
        sortBy={params.sortBy as SortField}
        sortOrder={params.sortOrder as SortOrder}
        onSort={handleSort}
        hasFilters={hasActiveFilters}
        onCreateClick={canCreate ? handleCreateClick : undefined}
        onFetchAllRows={() =>
          fetchAllForExport(async ({ limit: exportLimit }) => {
            const response = await rawProductsService.list({
              page: 1,
              limit: exportLimit,
              search: debouncedSearch || undefined,
              categoryId: params.categoryId || undefined,
              marketTypes: marketTypesArray.length > 0 ? marketTypesArray : undefined,
              isActive: isActiveValue,
              sortBy: params.sortBy as SortField,
              sortOrder: params.sortOrder as SortOrder,
            });
            return {
              items: response.data,
              pagination: { total: response.pagination.total },
            };
          })
        }
        showFinancials={showFinancials}
      />

      {/* Drawer */}
      <RawProductDrawer
        productId={drawer.resourceId}
        open={drawer.isOpen}
        onOpenChange={handleDrawerClose}
        canEdit={canCreate}
        showFinancials={showFinancials}
      />

      {/* Create Modal */}
      {canCreate && (
        <CreateRawProductModal
          isOpen={isCreateModalOpen}
          onClose={handleCreateModalClose}
          onSuccess={handleCreateSuccess}
        />
      )}
    </div>
  );
}
